import { EmailSignupService } from './email-signup.service'
import { DrawerWidgetState } from './session-storage.service'
import { SessionStorageService } from './session-storage.service'

type DrawerWidgetBehavior = 'dismissable' | 'dismissible' | 'minimizable'

type DrawerWidgetConfig = {
  widget_id: number | string
  trigger_scroll_percentage: number
  hide_scroll_percentage?: number | null
  minimize_scroll_percentage?: number | null
  behavior: DrawerWidgetBehavior
  content_config?: Record<string, unknown>
}

const SELECTORS = {
  close: '.drawer-widget__close',
  minimize: '.drawer-widget__minimize',
  expand: '.drawer-widget__expand',
  form: '.drawer-widget__form',
  inner: '.drawer-widget__inner',
  minimizedInner: '.drawer-widget__minimized',
  message: '.drawer-widget__message'
}

const PERCENT_MULTIPLIER = 100

const CLASS_NAMES = {
  visible: 'drawer-widget--visible',
  minimized: 'drawer-widget--minimized',
  success: 'drawer-widget__success',
  error: 'drawer-widget__error',
  inputError: 'drawer-widget__input--error',
  message: 'drawer-widget__message'
}

export class DrawerWidgetComponent {
  private config: DrawerWidgetConfig | undefined
  private isVisible = false
  private isMinimized = false
  private isDismissed = false
  private hasTriggered = false
  private isManuallyMinimized = false
  private hasUserOverriddenAutoMinimize = false
  private boundScrollHandler: EventListener | undefined
  private isScrollTicking = false

  constructor(private element: HTMLElement) {
    this.init()
  }

  private init(): void {
    this.parseConfig()
    this.loadStateFromStorage()

    if (this.isDismissed) {
      this.element.hidden = true
      this.element.classList.remove(CLASS_NAMES.visible)
      return
    }

    if (!this.config) {
      return
    }

    this.setupScrollMonitoring()
    this.setupEventListeners()
  }

  private parseConfig(): void {
    const configAttr = this.element.getAttribute('data-widget-config')
    if (!configAttr) {
      this.config = undefined
      return
    }

    try {
      this.config = JSON.parse(configAttr) as DrawerWidgetConfig
    } catch {
      this.config = undefined
    }
  }

  private setupScrollMonitoring(): void {
    const handler = this.onScroll.bind(this) as EventListener
    this.boundScrollHandler = handler
    window.addEventListener('scroll', handler, { passive: true })
    this.onScroll()
  }

  private onScroll(): void {
    if (this.isScrollTicking) return
    this.isScrollTicking = true

    requestAnimationFrame(() => {
      this.handleScroll()
      this.isScrollTicking = false
    })
  }

  private handleScroll(): void {
    if (!this.config) return

    const scrollPercentage = this.getScrollPercentage()
    const { trigger_scroll_percentage, hide_scroll_percentage, minimize_scroll_percentage } = this.config

    if (typeof hide_scroll_percentage === 'number' && scrollPercentage >= hide_scroll_percentage) {
      if (this.isVisible) {
        this.hide()
      }
      return
    }

    if (scrollPercentage >= trigger_scroll_percentage) {
      if (!this.isVisible && !this.isDismissed) {
        this.hasTriggered = true
        this.show()
      }
    } else {
      if (this.isVisible && this.hasTriggered) {
        this.hide()
      }

      if (this.hasUserOverriddenAutoMinimize) {
        this.resetAutoMinimizeOverride()
      }
    }

    const canAutoMinimize =
      typeof minimize_scroll_percentage === 'number' &&
      this.isVisible &&
      !this.isDismissed &&
      !this.isManuallyMinimized &&
      !this.hasUserOverriddenAutoMinimize

    if (!canAutoMinimize) {
      return
    }

    if (scrollPercentage >= minimize_scroll_percentage && !this.isMinimized) {
      this.minimize()
    } else if (scrollPercentage < minimize_scroll_percentage && this.isMinimized) {
      this.expand()
    }
  }

  private getScrollPercentage(): number {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop
    const scrollHeight = document.documentElement.scrollHeight - window.innerHeight
    return scrollHeight > 0 ? (scrollTop / scrollHeight) * PERCENT_MULTIPLIER : 0
  }

  private setupEventListeners(): void {
    const closeBtn = this.getElement<HTMLButtonElement>(SELECTORS.close)
    if (closeBtn) {
      closeBtn.addEventListener('click', () => this.dismiss())
    }

    const minimizeBtn = this.getElement<HTMLButtonElement>(SELECTORS.minimize)
    if (minimizeBtn) {
      minimizeBtn.addEventListener('click', () => this.minimizeManually())
    }

    const expandBtn = this.getElement<HTMLButtonElement>(SELECTORS.expand)
    if (expandBtn) {
      expandBtn.addEventListener('click', () => this.expandManually())
    }

    const form = this.getElement<HTMLFormElement>(SELECTORS.form)
    if (form) {
      form.addEventListener('submit', async (event) => {
        await this.handleFormSubmit(event)
      })
    }
  }

  private getElement<T extends Element>(selector: string): T | undefined {
    return (this.element.querySelector(selector) as T | null) || undefined
  }

  private show(): void {
    if (this.isDismissed || this.isVisible) return

    this.element.hidden = false
    this.element.classList.add(CLASS_NAMES.visible)
    this.isVisible = true

    if (this.isBehaviorMinimizable() && this.isMinimized) {
      this.applyMinimizedDomState()
      return
    }

    this.applyExpandedDomState()
  }

  private hide(): void {
    this.element.classList.remove(CLASS_NAMES.visible)
    this.isVisible = false
  }

  private isBehaviorMinimizable(): boolean {
    return this.config?.behavior === 'minimizable'
  }

  private minimize(): void {
    if (!this.isBehaviorMinimizable()) return

    this.isMinimized = true
    this.applyMinimizedDomState()
    this.saveStateToStorage()
  }

  private expand(): void {
    if (!this.isMinimized) return

    this.isMinimized = false
    this.applyExpandedDomState()
    this.saveStateToStorage()
  }

  private applyMinimizedDomState(): void {
    this.element.classList.add(CLASS_NAMES.minimized)

    const content = this.getElement<HTMLElement>(SELECTORS.inner)
    const minimized = this.getElement<HTMLElement>(SELECTORS.minimizedInner)
    if (content) content.hidden = true
    if (minimized) minimized.hidden = false

    // aria-expanded on the button (valid role) communicates collapsed state.
    const expandBtn = this.getElement<HTMLButtonElement>(SELECTORS.expand)
    if (expandBtn) expandBtn.setAttribute('aria-expanded', 'false')
  }

  private applyExpandedDomState(): void {
    this.element.classList.remove(CLASS_NAMES.minimized)

    const content = this.getElement<HTMLElement>(SELECTORS.inner)
    const minimized = this.getElement<HTMLElement>(SELECTORS.minimizedInner)
    if (content) content.hidden = false
    if (minimized) minimized.hidden = true

    // aria-expanded on the button (valid role) communicates expanded state.
    const expandBtn = this.getElement<HTMLButtonElement>(SELECTORS.expand)
    if (expandBtn) expandBtn.setAttribute('aria-expanded', 'true')
  }

  private minimizeManually(): void {
    this.isManuallyMinimized = true
    this.hasUserOverriddenAutoMinimize = false
    this.minimize()
  }

  private expandManually(): void {
    if (this.isMinimized && !this.isManuallyMinimized) {
      this.hasUserOverriddenAutoMinimize = true
    }

    this.isManuallyMinimized = false
    this.expand()
  }

  private dismiss(): void {
    this.isDismissed = true
    this.isManuallyMinimized = false
    this.hasUserOverriddenAutoMinimize = false

    this.hide()
    this.element.hidden = true
    this.removeScrollMonitoring()
    this.saveStateToStorage()
  }

  private async handleFormSubmit(event: Event): Promise<void> {
    event.preventDefault()

    const form = event.target as HTMLFormElement
    const emailInput = form.querySelector('input[name="email"]') as HTMLInputElement | null
    if (!emailInput) return

    const email = emailInput.value.trim()
    if (!email) {
      this.showErrorMessage('Please enter a valid email address.')
      return
    }

    const emailService = EmailSignupService.getInstance()
    const contentConfig = this.getContentConfig()
    const signupConfig = EmailSignupService.createDrawerWidgetConfig(
      String(this.config?.widget_id || 'unknown'),
      contentConfig
    )

    const result = await emailService.submitSignup(email, signupConfig)
    if (result.success) {
      this.showSuccessMessage(result.message)
      emailInput.value = ''
      return
    }

    this.showErrorMessage(result.message || 'Unable to process your subscription. Please try again.')
  }

  private getContentConfig(): Record<string, unknown> {
    if (!this.config?.content_config || typeof this.config.content_config !== 'object') {
      return {}
    }

    return this.config.content_config
  }

  private clearInlineMessages(): void {
    const messages = this.element.querySelectorAll(SELECTORS.message)
    messages.forEach((messageNode) => messageNode.remove())

    const errorInputs = this.element.querySelectorAll(`.${CLASS_NAMES.inputError}`)
    errorInputs.forEach((el) => el.classList.remove(CLASS_NAMES.inputError))
  }

  private showSuccessMessage(message?: string): void {
    const emailInput = this.element.querySelector('input[name="email"]') as HTMLInputElement | null
    if (!emailInput) return

    const contentConfig = this.getContentConfig()
    const configuredMessage = contentConfig.success_message
    const fallbackMessage =
      typeof configuredMessage === 'string' && configuredMessage.trim().length > 0
        ? configuredMessage
        : 'Thank you for signing up!'

    this.renderInlineMessage('success', message || fallbackMessage, emailInput)
  }

  private showErrorMessage(message: string): void {
    const emailInput = this.element.querySelector('input[name="email"]') as HTMLInputElement | null
    if (!emailInput) return

    this.renderInlineMessage('error', message, emailInput)
  }

  private renderInlineMessage(type: 'success' | 'error', message: string, anchorEl: HTMLElement): void {
    const form = this.getElement<HTMLFormElement>(SELECTORS.form)
    if (!form) return

    this.clearInlineMessages()

    if (type === 'error') {
      anchorEl.classList.add(CLASS_NAMES.inputError)
    }

    const messageEl = document.createElement('div')
    messageEl.className = `${CLASS_NAMES.message} ${type === 'success' ? CLASS_NAMES.success : CLASS_NAMES.error}`
    messageEl.textContent = message
    messageEl.setAttribute('role', type === 'error' ? 'alert' : 'status')
    messageEl.setAttribute('aria-live', type === 'error' ? 'assertive' : 'polite')

    const inputContainer = anchorEl.parentElement
    const insertionParent = inputContainer?.parentElement || form
    if (inputContainer) {
      insertionParent.insertBefore(messageEl, inputContainer.nextSibling)
      return
    }

    form.appendChild(messageEl)
  }

  private resetAutoMinimizeOverride(): void {
    this.hasUserOverriddenAutoMinimize = false
    this.saveStateToStorage()
  }

  private getStorageId(): string {
    return String(this.config?.widget_id || this.element.id || 'unknown')
  }

  private loadStateFromStorage(): void {
    const state = SessionStorageService.getInstance().get<DrawerWidgetState>(this.getStorageId())
    if (!state) return

    this.isDismissed = Boolean(state.dismissed)
    this.isMinimized = Boolean(state.minimized)
    this.isManuallyMinimized = Boolean(state.manuallyMinimized)
    this.hasUserOverriddenAutoMinimize = Boolean(state.userOverrodeAuto)
  }

  private saveStateToStorage(): void {
    const state: DrawerWidgetState = {
      dismissed: this.isDismissed,
      minimized: this.isMinimized,
      manuallyMinimized: this.isManuallyMinimized,
      userOverrodeAuto: this.hasUserOverriddenAutoMinimize
    }
    SessionStorageService.getInstance().set(this.getStorageId(), state)
  }

  private removeScrollMonitoring(): void {
    if (!this.boundScrollHandler) return

    window.removeEventListener('scroll', this.boundScrollHandler)
    this.boundScrollHandler = undefined
  }
}
